import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { MarketplaceDisplayBadge } from '__generated__/zeus/types';
import { corePdpLink, getShareableLink } from 'components/helpers/links';
import type { MarketplaceFavoriteOrigin } from 'components/marketplace/helpers/__generated__/marketplace-favorites.zeus';
import { browseListingStatus } from 'components/marketplace/helpers/browseListingStatus';
import { dollarsToCents } from 'components/marketplace/helpers/money';
import {
  type MarketplaceFavoriteChangeHandler,
  useMarketplaceFavorite,
} from 'components/marketplace/helpers/useMarketplaceFavorite';
import { MapListingCard } from 'components/marketplace/map/refresh/MapListingCard';
import { createMarketplaceQueryCache } from 'components/marketplace/queryCache';
import { CopyLinkToast } from 'components/marketplace/shared/CopyLinkToast';
import { ListingStatusBadge } from 'components/marketplace/shared/ListingStatusBadge';
import { useMediaQuery } from 'components/shared/hooks/useMediaQuery';
import { useObservability } from 'helpers/observability';
import dynamic from 'next/dynamic';

import { CardLink, PropertyCard } from '../PropertyCard';
import { streetViewLocationFor, StreetViewPhoto } from '../shared/StreetViewPhoto';
import { buildAddressLines, buildPropertyPhotoUrl, convertDwellingType } from './helpers';
import { ListingCardPricingDetails } from './ListingCardPricingDetails';
import type { ListingPreviewPhoto } from './PropertyCardGallery';
import { PdpSimilarListingCard } from './refresh/PdpSimilarListingCard';
import type { ShareableProperty } from './ShareModal';
import type { ListingCardData } from './types';

import styles from './SimilarListings.module.css';

// SSR must render the same photo tree that hydrates; viewport checks belong on its controls.
const PropertyCardGallery = dynamic(() =>
  import('./PropertyCardGallery').then((module) => module.PropertyCardGallery),
);
const ShareModal = dynamic(() => import('./ShareModal').then((module) => module.ShareModal));

const EMPTY_PHOTO_URLS: ReadonlyArray<string> = [];
const MAP_PHOTO_PREFETCH_DISTANCE_PX = 1200;
const DEFAULT_PHOTO_PREFETCH_DISTANCE_PX = 200;

export type PhotoLoadMode = 'deferred' | 'priority';

type UseListingPhotoSourceOptions = {
  hasPhoto: boolean;
  loadMode: PhotoLoadMode;
  preloadCarousel: boolean;
};

const useListingPhotoSource = ({
  hasPhoto,
  loadMode,
  preloadCarousel,
}: UseListingPhotoSourceOptions) => {
  const [shouldLoadPhoto, setShouldLoadPhoto] = useState(loadMode === 'priority');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || shouldLoadPhoto) {
      return undefined;
    }
    const root = containerRef.current.closest('[data-map-results-scroll]');
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoadPhoto(true);
          observer.disconnect();
        }
      },
      {
        root,
        rootMargin: root
          ? `${MAP_PHOTO_PREFETCH_DISTANCE_PX}px 0px`
          : `${DEFAULT_PHOTO_PREFETCH_DISTANCE_PX}px`,
      },
    );
    const visibilityTarget = preloadCarousel
      ? (containerRef.current.closest('[data-similar-homes-carousel]') ?? containerRef.current)
      : containerRef.current;
    observer.observe(visibilityTarget);
    return () => observer.disconnect();
  }, [preloadCarousel, shouldLoadPhoto]);

  return {
    containerRef,
    isPhotoPending: !shouldLoadPhoto && hasPhoto,
    shouldLoadPhoto,
  };
};

interface Tracking {
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
}

interface SimilarListingsProps {
  listings: ListingCardData[];
  title?: string | null;
  wide?: boolean;
}

interface ListingCardComponentProps {
  listing: ListingCardData;
  displayBadge?: MarketplaceDisplayBadge;
  photoLoadMode?: PhotoLoadMode;
  /** Desktop search cards opt into an interactive photo gallery; other surfaces stay static. */
  photoMode?: 'gallery' | 'static';
  contained?: boolean;
  favoriteOrigin?: MarketplaceFavoriteOrigin;
  initialFavoritePending?: boolean;
  onFavoriteChange?: MarketplaceFavoriteChangeHandler;
  variant?: 'default' | 'search-result' | 'pdp-refresh' | 'map-refresh';
  detailsLayout?: 'default' | 'address-first';
  onOpen?: (listing: ListingCardData, origin: HTMLElement, photo?: ListingPreviewPhoto) => void;
}

const ListingPhotoOverlay = ({ displayBadge }: { displayBadge: MarketplaceDisplayBadge }) => (
  <Row className="pointer-events-none absolute left-2 right-2 top-2 z-1">
    <ListingStatusBadge displayBadge={displayBadge} />
  </Row>
);

type ShareModalState = 'unopened' | 'open' | 'closed';
/**
 * `unused` keeps the toast unmounted until the first copy: the toast needs the page's `<Toasty>`,
 * and only the map page, which has one, renders `map-refresh` cards.
 */
type CopyToastState = 'unused' | 'open' | 'shown';

export const ListingCard: React.FC<ListingCardComponentProps & Tracking> = ({
  utmSource,
  utmMedium,
  utmCampaign,
  utmContent,
  utmTerm,
  listing,
  displayBadge,
  photoLoadMode = 'deferred',
  photoMode = 'static',
  contained = false,
  favoriteOrigin = 'LISTING_DETAILS',
  initialFavoritePending,
  onFavoriteChange,
  variant = 'default',
  detailsLayout = 'default',
  onOpen,
}) => {
  const photoRef = useRef<HTMLDivElement>(null);
  const displayedPhotoRef = useRef<ListingPreviewPhoto | null>(null);
  const handlePhotoChange = useCallback((photo: ListingPreviewPhoto | null) => {
    displayedPhotoRef.current = photo;
  }, []);
  const handleOpen = (event: React.MouseEvent<HTMLAnchorElement>, photo?: ListingPreviewPhoto) => {
    if (
      !onOpen ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    event.preventDefault();
    onOpen(
      listing,
      photoRef.current ?? event.currentTarget,
      photo ??
        displayedPhotoRef.current ??
        (listing.photos[0] ? { url: listing.photos[0], index: 0 } : undefined),
    );
  };
  const isMapRefresh = variant === 'map-refresh';
  const isSearchResult = variant === 'search-result' || isMapRefresh;
  const isPdpRefresh = variant === 'pdp-refresh';
  let galleryVariant: 'default' | 'map-refresh' | 'pdp-refresh' = 'default';
  if (isMapRefresh) galleryVariant = 'map-refresh';
  if (isPdpRefresh) galleryVariant = 'pdp-refresh';
  const [shareModalState, setShareModalState] = useState<ShareModalState>('unopened');
  const [copyToastState, setCopyToastState] = useState<CopyToastState>('unused');
  const { trackJourneyEvent } = useObservability();
  const { isFavoriteLoading, isLiked, onToggleFavorite } = useMarketplaceFavorite({
    addressId: listing.addressToken,
    origin: favoriteOrigin,
    initialIsLiked: listing.isLiked,
    initialFavoritePending,
    onFavoriteChange,
    priceCents: listing.listPrice == null ? null : dollarsToCents(listing.listPrice),
  });

  const { containerRef, isPhotoPending, shouldLoadPhoto } = useListingPhotoSource({
    hasPhoto: listing.photos.length > 0,
    loadMode: photoLoadMode,
    preloadCarousel: isPdpRefresh,
  });
  const photoUrl = buildPropertyPhotoUrl(listing.photos[0], {
    preset: '3:2-md',
    presetScale: '2/3',
    dpr: '1',
  });

  const query = new URLSearchParams({
    ...(utmSource && { utm_source: utmSource }),
    ...(utmMedium && { utm_medium: utmMedium }),
    ...(utmCampaign && { utm_campaign: utmCampaign }),
    ...(utmContent && { utm_content: utmContent }),
    ...(utmTerm && { utm_term: utmTerm }),
  }).toString();
  const propertyLink = corePdpLink(listing.slug ?? '_', listing.addressToken);
  const link = `${propertyLink}${query ? `?${query}` : ''}`;

  const { primaryLine, secondaryLine } = buildAddressLines(listing);

  // Photography gates going live, so available-soon homes are photo-less by
  // construction — street view stands in when we have the geo point.
  const isAvailableSoon = listing.listingState === 'AVAILABLE_SOON';
  const streetViewLocation = streetViewLocationFor({
    availableSoon: isAvailableSoon,
    photoCount: listing.photos.length,
    latitude: listing.latitude,
    longitude: listing.longitude,
  });

  let photoMedia: React.ReactElement | undefined;
  if (streetViewLocation) {
    photoMedia = (
      <CardLink
        href={link}

        external={isSearchResult}
        ariaLabel="Main view of property"
        analyticsName="marketplace-property-card-image-link"
        onClick={onOpen ? handleOpen : undefined}
        className="h-full w-full"
      >
        <StreetViewPhoto
          latitude={streetViewLocation.latitude}
          longitude={streetViewLocation.longitude}
          alt={`Street view of ${listing.fullAddress ?? 'this home'}`}
          className="aspect-[16/9] w-full"
          fallbackLabel="Photos coming soon"
        />
      </CardLink>
    );
  } else if (photoMode === 'gallery' || isPdpRefresh || isMapRefresh) {
    photoMedia = (
      <PropertyCardGallery
        key={listing.addressToken}
        variant={galleryVariant}
        addressId={listing.addressToken}
        href={link}
        onOpen={onOpen ? handleOpen : undefined}
        onPhotoChange={onOpen ? handlePhotoChange : undefined}
        isPhotoPending={isPhotoPending}
        openInNewTab={isSearchResult}
        placeholderLabel={isAvailableSoon ? 'Photos coming soon' : undefined}
        previewPhotoUrls={shouldLoadPhoto ? listing.photos : EMPTY_PHOTO_URLS}
        fetchPriority={photoLoadMode === 'priority' ? 'high' : undefined}
      />
    );
  }

  const listingStatus = displayBadge ? browseListingStatus(displayBadge) : null;
  const showListingStatus = isSearchResult
    ? listingStatus === 'coming-soon' || listingStatus === 'self-tour'
    : listingStatus != null;
  const handleOpenShareModal = useCallback(() => setShareModalState('open'), []);
  const handleCloseShareModal = useCallback(() => setShareModalState('closed'), []);
  const handleCopyLink = useCallback(() => {
    trackJourneyEvent('marketplaceProperty', 'share', {
      addressUuid: listing.addressToken,
      source: 'copy',
    });
    if (navigator.clipboard) {
      void navigator.clipboard.writeText(
        getShareableLink(listing.slug ?? '_', listing.addressToken),
      );
    }
    setCopyToastState('open');
  }, [listing.addressToken, listing.slug, trackJourneyEvent]);
  const handleCopyToastShown = useCallback(() => setCopyToastState('shown'), []);
  const shareableProperty = useMemo<ShareableProperty>(
    () => ({
      addressToken: listing.addressToken,
      slug: listing.slug ?? '_',
      street: listing.street ?? null,
      unitNumber: null,
      unitType: null,
    }),
    [listing.addressToken, listing.slug, listing.street],
  );

  let refreshCard: React.ReactElement | null = null;
  if (isMapRefresh) {
    refreshCard = (
      <MapListingCard
        listing={listing}
        href={link}
        media={photoMedia}
        photoRef={photoRef}
        onOpen={onOpen ? handleOpen : undefined}
        isLiked={isLiked}
        isFavoriteLoading={isFavoriteLoading}
        onToggleFavorite={onToggleFavorite}
        onCopyLink={handleCopyLink}
      />
    );
  } else if (isPdpRefresh) {
    refreshCard = (
      <PdpSimilarListingCard
        listing={listing}
        href={link}
        media={photoMedia}
        isLiked={isLiked}
        isFavoriteLoading={isFavoriteLoading}
        onToggleFavorite={onToggleFavorite}
      />
    );
  }

  return (
    <Col ref={containerRef} className={contained ? 'h-full w-full' : 'w-full'}>
      {refreshCard ?? (
        <PropertyCard
          contained={contained}
          actionsOnPhoto={detailsLayout === 'address-first'}
          details={
            detailsLayout === 'address-first' ? (
              <ListingCardPricingDetails
                listing={listing}
                href={link}

                openInNewTab={isSearchResult}
              />
            ) : undefined
          }
          baths={listing.baths}
          beds={listing.beds}
          city={secondaryLine}
          dwellingType={convertDwellingType(listing.dwellingType)}
          href={link}

          openLinksInNewTab={isSearchResult}
          isFavorite={isLiked}
          isFavoriteLoading={isFavoriteLoading}
          neighborhood={primaryLine}
          onFavoritePress={onToggleFavorite}
          onSharePress={isSearchResult ? handleOpenShareModal : undefined}
          isPhotoPending={isPhotoPending}
          photoMedia={photoMedia}
          photoOverlay={
            displayBadge && showListingStatus ? (
              <ListingPhotoOverlay displayBadge={displayBadge} />
            ) : undefined
          }
          photoUrl={shouldLoadPhoto ? photoUrl : null}
          photoFetchPriority={photoLoadMode === 'priority' ? 'high' : undefined}
          price={listing.listPrice}
          selfTourAvailable={displayBadge === undefined ? listing.canSelfTour : false}
          statusLabel={isAvailableSoon ? 'Available soon' : null}
          sqft={listing.livingSquareFootage}
        />
      )}
      {isSearchResult && shareModalState !== 'unopened' ? (
        <ShareModal
          property={shareableProperty}
          displayAddress={listing.fullAddress}
          isOpen={shareModalState === 'open'}
          onClose={handleCloseShareModal}
        />
      ) : null}
      {copyToastState === 'unused' ? null : (
        <CopyLinkToast
          open={copyToastState === 'open'}
          message="Link copied to clipboard"
          onShown={handleCopyToastShown}
        />
      )}
    </Col>
  );
};

const SimilarListingsCarousel = ({
  cards,
  title,
}: {
  cards: React.ReactElement[];
  title: string;
}) => {
  const isLargeScreen = useMediaQuery('(min-width: 1201px)');
  const isTabletOrLarger = useMediaQuery('(min-width: 640px)');
  const cardsPerPage = isLargeScreen ? 4 : 2;
  const [firstCardIndex, setFirstCardIndex] = useState(0);
  const pageCount = Math.ceil(cards.length / cardsPerPage);
  const pageIndex = Math.min(Math.floor(firstCardIndex / cardsPerPage), Math.max(0, pageCount - 1));
  const changePage = useCallback(
    (nextPage: number) =>
      setFirstCardIndex(Math.min(Math.max(nextPage, 0), Math.max(0, pageCount - 1)) * cardsPerPage),
    [cardsPerPage, pageCount],
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasMoreHomes, setHasMoreHomes] = useState(false);
  const updateOverflow = useCallback(() => {
    const scroll = scrollRef.current;
    if (scroll) {
      setHasMoreHomes(scroll.scrollWidth - scroll.clientWidth - scroll.scrollLeft > 1);
    }
  }, []);

  useEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll) return undefined;
    if (isTabletOrLarger) scroll.scrollLeft = 0;
    updateOverflow();
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(scroll);
    const lastCard = scroll.firstElementChild?.children.item(cards.length - 1);
    if (lastCard) observer.observe(lastCard);
    return () => observer.disconnect();
  }, [cards.length, isTabletOrLarger, updateOverflow]);

  return (
    <Col className="w-full gap-5" data-similar-homes-carousel>
      <Row className="items-center justify-between gap-4">
        <Text as="h2" className="text-label-lg text-od-content-primary">
          {title}
        </Text>
        {isTabletOrLarger && pageCount > 1 && (
          <Row className="shrink-0 gap-1">
            <Button
              variant="secondary"
              size="sm"
              analyticsName="pdp-similar-homes-previous"
              aria-label="Previous homes"
              aria-disabled={pageIndex === 0}
              onClick={() => changePage(pageIndex - 1)}
              className={`size-8 rounded-full p-0 ${styles.pageButton}`}
            >
              <IconChevronLeft size={18} aria-hidden />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              analyticsName="pdp-similar-homes-next"
              aria-label="Next homes"
              aria-disabled={pageIndex === pageCount - 1}
              onClick={() => changePage(pageIndex + 1)}
              className={`size-8 rounded-full p-0 ${styles.pageButton}`}
            >
              <IconChevronRight size={18} aria-hidden />
            </Button>
          </Row>
        )}
      </Row>
      <Box className="relative min-w-0">
        <Box
          ref={scrollRef}
          role="group"
          aria-label="Similar homes carousel"
          onScroll={updateOverflow}
          className={styles.viewport}
        >
          <Row
            className={styles.track}
            ref={(node) => {
              node?.style.setProperty('--similar-homes-page', String(pageIndex));
            }}
          >
            {cards.map((card, index) => {
              const isHidden = isTabletOrLarger && Math.floor(index / cardsPerPage) !== pageIndex;
              return (
                <Col
                  key={card.key}
                  aria-hidden={isHidden || undefined}
                  ref={(node) => {
                    node?.toggleAttribute('inert', isHidden);
                  }}
                  className={styles.card}
                >
                  {card}
                </Col>
              );
            })}
          </Row>
        </Box>
        <Box
          aria-hidden="true"
          data-similar-homes-fade
          data-visible={hasMoreHomes}
          className={`pointer-events-none absolute inset-y-0 right-0 z-3 w-12 bg-gradient-to-r from-transparent via-od-bg-primary/75 to-od-bg-primary backdrop-blur-[1px] [mask-image:linear-gradient(to_right,transparent,black)] transition-opacity duration-200 motion-reduce:transition-none sm:hidden ${hasMoreHomes ? 'opacity-100' : 'opacity-0'}`}
        />
      </Box>
      {isTabletOrLarger && (
        <Text as="p" aria-live="polite" className="sr-only">
          Page {pageIndex + 1} of {pageCount}
        </Text>
      )}
    </Col>
  );
};

const SimilarListingsContent: React.FC<SimilarListingsProps & Tracking> = ({
  listings,
  title,
  wide = false,
  utmSource,
  utmMedium,
  utmCampaign,
  utmContent,
  utmTerm,
}) => {
  const displayedListingSetCount = wide ? 8 : 6;
  const [displayedListingCount, setDisplayedListingCount] =
    React.useState(displayedListingSetCount);

  const incrementDisplayedListingCount = useCallback(() => {
    setDisplayedListingCount((prev) => prev + displayedListingSetCount);
  }, [displayedListingSetCount]);

  const similarListings = listings.slice(0, displayedListingCount).map((listing) => (
    <Col key={listing.addressToken} className={wide ? 'min-w-0' : 'w-full pb-6 lg:w-[48%]'}>
      <ListingCard
        listing={listing}
        variant={wide ? 'pdp-refresh' : 'default'}
        contained={wide}
        utmSource={utmSource}
        utmMedium={utmMedium}
        utmCampaign={utmCampaign}
        utmContent={utmContent}
        utmTerm={utmTerm}
      />
    </Col>
  ));

  if (wide) {
    return <SimilarListingsCarousel cards={similarListings} title={title || 'Similar listings'} />;
  }

  return (
    <Col className="pb-31 md:pb-6">
      <Text as="h5" className="pb-6 text-subheader-xs text-od-content-primary">
        {title || 'Similar listings'}
      </Text>
      <Col className="flex-wrap justify-between lg:flex-row">{similarListings}</Col>
      {displayedListingCount < listings.length && (
        <Button
          onClick={incrementDisplayedListingCount}
          variant="secondary"
          size="lg"
          analyticsName="marketplace-show-more-listings"
          aria-label="See more homes"
          className="w-full"
        >
          See more homes
        </Button>
      )}
    </Col>
  );
};

const SimilarListingsWithGallery = (props: SimilarListingsProps & Tracking) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: 1 } },
        queryCache: createMarketplaceQueryCache(),
      }),
  );
  useEffect(() => () => queryClient.clear(), [queryClient]);
  return (
    <QueryClientProvider client={queryClient}>
      <SimilarListingsContent {...props} />
    </QueryClientProvider>
  );
};

export const SimilarListings = (props: SimilarListingsProps & Tracking) =>
  props.wide ? <SimilarListingsWithGallery {...props} /> : <SimilarListingsContent {...props} />;
